import { LOCALIZED_PATHS } from '../../../utils/constants/url-constants';
import { PRIMARY_NAV_LINKS, primaryNavHref, type PrimaryNavLocale } from '../primary-nav-links';

/**
 * Keys the index strip can highlight. Distinct from the figure keys in useMenuPulse: `newToday`
 * is never its own active section (a "new today" click lands on the same /jobs hub as
 * `openJobs`, just with a filter applied), so it never appears here.
 */
export type NavActiveSection = 'openJobs' | 'remote' | 'roles' | 'employers' | 'locations' | 'salary' | 'resources';

function findLink(key: string) {
  const link = PRIMARY_NAV_LINKS.find((l) => l.key === key);
  if (!link) throw new Error(`Unknown primary nav link key: ${key}`);
  return link;
}

function linkHref(key: string, locale: PrimaryNavLocale): string {
  return primaryNavHref(findLink(key), locale);
}

function stripQueryAndHash(pathname: string): string {
  return pathname.split('?')[0]?.split('#')[0] ?? pathname;
}

function stripTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Resolves which figure/word link in the navbar index strip should be highlighted for the
 * current pathname: /jobs* -> openJobs, but the more specific /jobs/remote -> remote and
 * /jobs/roles* -> roles; /best-companies* and /company/{slug} -> employers; /locations* ->
 * locations; /salary* -> salary; /resources* -> resources. Locale-aware: matches each locale's
 * own localized segments (e.g. fi /tyopaikat/etatyo, sv /lediga-jobb/yrken), via the same
 * PRIMARY_NAV_LINKS + localizeRoute pipeline the strip's own hrefs are built from, so a match
 * here can never drift from what the strip actually links to. Returns null off every hub (e.g.
 * the marketing home page).
 *
 * `pathname` is matched against three shapes per section, not just the fully localized,
 * locale-prefixed href a real browser URL bar shows: `web-app/pages/+onBeforeRoute.ts` (which
 * every route in this app goes through) either strips only the locale prefix and keeps the
 * localized segment (resource/job-search paths: fi "/en/resources" -> onBeforeRoute's
 * pageContext.urlLogical "/tyoelama") or fully delocalizes to the English logical segment (every
 * other path: fi "/fi/tyopaikat" -> urlLogical "/jobs"), and Vike's own `pageContext.urlPathname`
 * getter then resolves from whichever of those `onBeforeRoute` set, so a caller reading
 * `pageContext.urlPathname`/`urlLogical` (as `resolveNavPagePathname` in resolve-page-locale.ts
 * does, and as this codebase's own `isSearchPagePathname` already defends against for the SRP
 * check) will not reliably get the locale-prefixed form `primaryNavHref` builds. Matching all
 * three shapes (locale-prefixed localized, unprefixed localized, unprefixed English logical)
 * means this resolves correctly regardless of which shape the caller's pathname happens to be.
 */
export function resolveNavActiveSection(pathname: string, locale: PrimaryNavLocale): NavActiveSection | null {
  const cleaned = stripTrailingSlash(stripQueryAndHash(pathname || '/'));
  if (cleaned === '' || cleaned === '/' || cleaned === `/${locale}`) return null;

  const localePrefix = `/${locale}`;
  const matches = (href: string, logicalPath: string): boolean => {
    if (cleaned === href || cleaned.startsWith(`${href}/`)) return true;
    const unprefixedLocalized = href.startsWith(`${localePrefix}/`) ? href.slice(localePrefix.length) : href;
    if (cleaned === unprefixedLocalized || cleaned.startsWith(`${unprefixedLocalized}/`)) return true;
    if (cleaned === logicalPath || cleaned.startsWith(`${logicalPath}/`)) return true;
    return false;
  };

  const employerProfileBase = `/${locale}/${LOCALIZED_PATHS[locale].company}`;
  const employerProfileLogical = `/${LOCALIZED_PATHS.en.company}`;

  // Most specific first: remote/roles are inside the jobs hub but must win over the bare
  // openJobs match; the employer profile segment and the employers index both resolve the same.
  if (matches(employerProfileBase, employerProfileLogical) || matches(linkHref('companies', locale), findLink('companies').logicalPath)) return 'employers';
  if (matches(linkHref('remote', locale), findLink('remote').logicalPath)) return 'remote';
  if (matches(linkHref('roles', locale), findLink('roles').logicalPath)) return 'roles';
  if (matches(linkHref('jobs', locale), findLink('jobs').logicalPath)) return 'openJobs';
  if (matches(linkHref('locations', locale), findLink('locations').logicalPath)) return 'locations';
  if (matches(linkHref('salary', locale), findLink('salary').logicalPath)) return 'salary';
  if (matches(linkHref('resources', locale), findLink('resources').logicalPath)) return 'resources';
  return null;
}
